// @flow
import * as React from 'react';
import { sprintf } from 'sprintf-js';
import styled, { css } from 'styled-components';
import type { FileUploadTypes } from 'api/shared';
import { isDefined } from 'utils/functional';
import { getRgbaString } from 'utils/style';
import { fileIdToUrl } from 'utils/s3file';
import { dateTime } from 'utils/date';
import Box from 'components/Box';
import S3Picker from 'containers/S3Picker';
import Spinner from 'components/Spinner';
import Button from 'components/Button';
import { Body, BodySmall } from 'components/Text';
import Icon from 'components/Icon';
import { colors, transition, mediaRetina } from '../theme';

const DeleteButton = styled(Button)`
  transition: all ${transition};
  &:hover,
  &:focus {
    color: ${colors.waterBlue};
  }
`;

const DropBox = styled(Box)`
  color: ${props =>
    props.$hasFile ? colors.battleshipGrey : colors.waterBlue};
  transition: all ${transition};
  & > * {
    position: relative;
    border: 1px solid
      ${props => (props.$hasFile ? colors.silver : colors.waterBlue)};
    border-radius: 45px;
    transition: all ${transition};
    @media ${mediaRetina} {
      border-width: 1.5px;
    }
  }
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 6px;
    opacity: 0;
    transition: all ${transition};
  }
`;

const DropContainer = styled(Box)`
  &,
  * {
    pointer-events: none;
  }
  ${DeleteButton}, a {
    pointer-events: auto;
  }
  ${props =>
    props.$mouseActive &&
    css`
      ${DropBox} {
        & > * {
          background: ${colors.waterBlue};
          border-color: ${colors.waterBlue};
          color: white;
        }
        &::before {
          opacity: 1;
        }
      }
    `}
  ${props =>
    props.$dragActive &&
    `
    * {
      pointer-events: none !important;
    }
  `}
`;

type Props = {
  downloadAttachment?: boolean,
  fileId: ?string,
  fileType: FileUploadTypes,
  message?: string,
  name: string,
  onChange: (?string) => void,
  hideRemove?: boolean,
  isRequired?: boolean,
  'data-qa-id': string,
  value?: string,
  hasError?: boolean,
  showDate?: number
};

const FilePicker = ({
  downloadAttachment,
  fileId,
  fileType,
  message = getText('Drag file here or browse'),
  name,
  onChange,
  'data-qa-id': qaId,
  hideRemove,
  isRequired = false,
  value,
  hasError,
  showDate,
  ...props
}: Props) => (
  <S3Picker
    downloadAttachment={downloadAttachment}
    isRequired={isRequired}
    fileId={fileId}
    fileType={fileType}
    name={name}
    onChange={onChange}
    qaId={qaId}
    value={value}
  >
    {({ onClick, filename, created, loading, dragActive, mouseActive }) => (
      <DropContainer
        position="relative"
        display="inline-flex"
        width={1}
        flexWrap="wrap"
        alignItems="center"
        pointerEvents="none"
        $hasFile={isDefined(fileId)}
        $hasError={hasError}
        $dragActive={dragActive}
        $mouseActive={mouseActive}
      >
        <Box
          position="absolute"
          left={-12}
          right={-12}
          top={-12}
          bottom={-12}
          borderRadius="4px"
          bg={`rgba(${getRgbaString(colors.babyBlue, 0.2)})`}
          opacity={mouseActive && !loading ? 1 : 0}
          transition={`opacity ${transition}`}
        />
        {loading ? (
          <Box
            width={32}
            height={65}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Spinner size="small" />
          </Box>
        ) : (
          <>
            <Box data-qa-id={`${qaId}-selector`} mx={-10}>
              <DropBox
                position="relative"
                borderRadius="4px"
                p={10}
                onClick={onClick}
                $hasFile={!!fileId}
                {...props}
              >
                <Box display="flex" alignItems="center" p="6px 16px">
                  <Box>
                    <Icon
                      name="paperclip"
                      display="block"
                      width={24}
                      fill="currentColor"
                    />
                  </Box>
                  <BodySmall my={0} ml="4px">
                    {message}
                  </BodySmall>
                </Box>
              </DropBox>
            </Box>
            <Box display="inline-block" ml={24} color={colors.battleshipGrey}>
              {fileId ? (
                <Box>
                  <Box
                    component={Body}
                    position="relative"
                    display="flex"
                    alignItems="center"
                    data-qa-id={`${qaId}-preview`}
                  >
                    <a
                      href={fileIdToUrl(fileId, downloadAttachment)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Box
                        maxWidth={200}
                        overflow="hidden"
                        whiteSpace="nowrap"
                        textOverflow="ellipsis"
                        verticalAlign="middle"
                      >
                        {filename}
                      </Box>
                    </a>

                    {!hideRemove && (
                      <Box
                        component={DeleteButton}
                        title={
                          /* TRANSLATORS: Remove file upload */ sprintf(
                            getText('Remove file %(filename)s'),
                            {
                              filename
                            }
                          )
                        }
                        data-qa-id={`${qaId}-delete`}
                        onClick={e => {
                          e.stopPropagation();
                          onChange(undefined);
                        }}
                        ml={10}
                      >
                        <Icon
                          name="close"
                          display="block"
                          width={10}
                          color="currentColor"
                        />
                      </Box>
                    )}
                  </Box>

                  {showDate && created && (
                    <Box width={1} flex="0 0 auto">
                      <BodySmall
                        color={colors.battleshipGrey}
                        whiteSpace="nowrap"
                        lineHeight={1.25}
                        data-qa-id={`${qaId}-upload-date`}
                      >
                        {sprintf(getText('Last updated: %(date)s'), {
                          date: dateTime(created)
                        })}
                      </BodySmall>
                    </Box>
                  )}
                </Box>
              ) : (
                getText('No file chosen')
              )}
            </Box>
          </>
        )}
      </DropContainer>
    )}
  </S3Picker>
);

export default FilePicker;
